import { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import FreestarAdSlot from "@freestar/pubfig-adslot-react-component";
import { AppRoutes } from "@ugg/shared/routes/app-routes";
import { FREESTAR_PUBLISHER, FreestarPlacements } from "./FreestarContainer";

const PLUS_UTM = "utm_source=uggbuilds";
const PLUS_EXTERNAL_URL = `https://u.gg/plus?${PLUS_UTM}`;

interface FreestarInContentContainerProps {
  className?: string;
  id?: string;
  unitId?: string;
  placement: FreestarPlacements;
  header?: string;
  showAdLabel?: boolean;
  showPlusLink?: boolean;
  slotClassName?: string;
  inNewWindow?: boolean;
  ignoreSkinAd?: boolean;
}

export const FreestarInContentContainer = ({
  className,
  id,
  unitId,
  placement,
  header,
  showAdLabel,
  showPlusLink,
  slotClassName,
  inNewWindow,
  ignoreSkinAd,
}: FreestarInContentContainerProps) => {
  const hasChrome = !!showAdLabel || !!showPlusLink;

  return (
    <div
      className={classNames(
        "flex flex-col rounded-[3px]",
        hasChrome ? "bg-purple-200 overflow-hidden" : "bg-[#191937] flex-column gap-y-[6px]",
        {
          "px-[18px] pb-[18px] pt-[12px]": !!header && !hasChrome,
          "p-[18px]": !header && !hasChrome,
        },
        className,
      )}
    >
      {hasChrome && (
        <div className="flex items-center justify-between px-[12px] py-[6px] text-[10px] leading-none bg-purple-400">
          {showAdLabel && <span className="text-lavender-50">Advertisement</span>}
          {showPlusLink && (
            <Link
              to={`${AppRoutes.PLUS}?${PLUS_UTM}`}
              onClick={
                inNewWindow
                  ? (e) => {
                      e.preventDefault();
                      window.open(PLUS_EXTERNAL_URL);
                    }
                  : undefined
              }
              className="ml-auto text-accent-yellow-400"
            >
              Go Ad-Free
            </Link>
          )}
        </div>
      )}
      {header && !hasChrome && <div className="text-white text-[14px] h-[18px]">{header}</div>}
      <FreestarInContent id={id} unitId={unitId} placement={placement} slotClassName={slotClassName} ignoreSkinAd={ignoreSkinAd} />
    </div>
  );
};

function useSkinAdDetection() {
  const polling = useRef<NodeJS.Timer>();
  const [skinAdActive, setSkinAdActive] = useState(false);

  useEffect(() => {
    polling.current = setInterval(() => {
      const skinDiv = document.getElementById("siteSkinContainer");
      setSkinAdActive(!!skinDiv);
    }, 1000);

    return () => {
      polling.current && clearInterval(polling.current);
    };
  }, []);

  return skinAdActive;
}

interface FreestarInContentProps {
  id?: string;
  unitId?: string;
  placement: FreestarPlacements;
  slotClassName?: string;
  ignoreSkinAd?: boolean;
}

const FreestarInContent = ({
  id = "ugg-in-content",
  unitId = "fs-in-content",
  placement,
  slotClassName = "h-[300px] w-full",
  ignoreSkinAd,
}: FreestarInContentProps) => {
  const skinAdActive = useSkinAdDetection();

  return (
    <div id={id} className="flex flex-col items-center w-full">
      {(ignoreSkinAd || !skinAdActive) && (
        <div className={slotClassName}>
          <FreestarAdSlot publisher={FREESTAR_PUBLISHER} placementName={placement} slotId={unitId} />
        </div>
      )}
    </div>
  );
};
